import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import MaxWidthPage from "components/MaxWidthPage";
import { RecentlyViewedProvider } from "providers/RecentlyViewed/RecentlyViewedProvider";
import { CrawlerPageLayout } from "pages/crawler/components/CrawlerPageLayout";
import Button from "components/Button";
import Toaster from "components/Toaster";
import CrawlerSummaryModal from "pages/crawler/components/form/CrawlerSummaryModal";
import type { CrawlerFormData } from "./types";
import { DEFAULT_CRAWLER_FORM_VALUES } from "./types";
import scraperApi from "api";
import { mapFormDataToCrawlerData, mapCrawlerDataToFormData, getErrorMessage } from "pages/crawler/utils/crawlerFormUtils";
import { useCrawler } from "providers/CrawlerProvider";
import { CrawlerCostProvider } from "pages/crawler/providers/CrawlerCostProvider";
import { useCrawlerForm } from "pages/crawler/hooks/useCrawlerForm";
import { validateCrawlerName, validateCrawlerLimits } from "pages/crawler/utils/crawlerValidation";
import { CrawlerUrlInput } from "pages/crawler/components/form/CrawlerUrlInput";
import { CrawlerFormSections } from "pages/crawler/components/form/CrawlerFormSections";

const EditCrawlerPage = () => {
  const [crawlerName, setCrawlerName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const navigate = useNavigate();
  const { id: crawlerId } = useParams();
  const { refreshCrawlers } = useCrawler();

  // Use shared crawler form hook
  const {
    isTestingCallback,
    callbackTestResult,
    setCallbackTestResult,
    testCallback: testCallbackFn,
    scrollToFirstError,
  } = useCrawlerForm();

  const methods = useForm<CrawlerFormData>({
    defaultValues: DEFAULT_CRAWLER_FORM_VALUES,
    mode: "onChange", // Enable real-time validation
    reValidateMode: "onChange", // Re-validate on change
  });

  const { register, watch, setValue, getValues, reset, trigger, formState: { errors } } = methods;
  const watchedValues = watch();

  useEffect(() => {
    const scheduleName = watchedValues?.schedule?.name;
    if (scheduleName && scheduleName !== crawlerName) {
      setCrawlerName(scheduleName);
    }
  }, [watchedValues?.schedule?.name, crawlerName]);

  useEffect(() => {
    const abortController = new AbortController();

    const loadCrawlerData = async () => {
      if (!crawlerId) return;

      setLoading(true);
      try {
        // Fetch crawler details from API with abort signal
        const response = await scraperApi.crawlers.getCrawlerById(crawlerId, {
          signal: abortController.signal
        });

        // Check if aborted before processing response
        if (abortController.signal.aborted) return;

        if (response.success && response.data) {
          const crawler = response.data;

          // Map backend crawler data to form format using utility function
          const formData = mapCrawlerDataToFormData(crawler);

          reset(formData);
          const derivedName = formData.schedule?.name || crawler.name || "";
          setCrawlerName(derivedName);
          if (!formData.schedule?.name) {
            setValue("schedule", { ...formData.schedule, name: derivedName }, { shouldDirty: false });
          }

          // Force set URL value and trigger validation since AdditionalOptionsTextBox might not sync properly
          setValue("url", formData.url, { shouldValidate: true, shouldDirty: true });

          // Trigger validation immediately after loading data to update isValid state
          await trigger();
        } else {
          throw new Error('Failed to load crawler data');
        }
      } catch (error: any) {
        // Ignore abort errors
        if (error.name === 'AbortError' || abortController.signal.aborted) {
          return;
        }

        console.error('Failed to load crawler data:', error);
        Toaster.error(getErrorMessage(error, 'Failed to load crawler data'));

        // Navigate back to crawlers list if crawler not found
        if (error.response?.status === 404) {
          navigate('/crawlers');
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadCrawlerData();

    // Cleanup: abort ongoing request if component unmounts
    return () => {
      abortController.abort();
    };
  }, [crawlerId, reset, navigate, trigger, setValue]);

  const onSubmit = async (data: CrawlerFormData) => {
    // Validate crawler name
    const nameError = validateCrawlerName(crawlerName);
    if (nameError) {
      Toaster.error(nameError);
      return;
    }

    // Validate crawler limits
    const limitsError = validateCrawlerLimits(data.linkDepth, data.maxCost);
    if (limitsError) {
      Toaster.error(limitsError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Use shared data mapping utility
      const crawlerData = mapFormDataToCrawlerData(data, crawlerName);

      await scraperApi.crawlers.updateCrawler(crawlerId!, crawlerData);

      Toaster.success(`Crawler "${crawlerName}" updated successfully!`);
      // Refresh the crawler list to show the updated crawler
      await refreshCrawlers();
      navigate('/crawlers');

    } catch (error: any) {
      console.error('Error updating crawler:', error);
      Toaster.error(getErrorMessage(error, 'Failed to update crawler'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCrawlerNameChange = (newName: string) => {
    setCrawlerName(newName);
    const currentSchedule = getValues("schedule");
    setValue("schedule", { ...currentSchedule, name: newName }, { shouldDirty: true, shouldValidate: true });
  };

  const testCallback = () => testCallbackFn(watchedValues.callbackUrl);

  const reviewChanges = async () => {
    // Trigger validation before showing summary
    const isFormValid = await trigger();

    if (!isFormValid) {
      scrollToFirstError();
      Toaster.error("Please fix the errors highlighted");
      return;
    }

    // Show summary modal for review
    setShowSummaryModal(true);
  };

  const confirmSaveChanges = () => {
    setShowSummaryModal(false);
    methods.handleSubmit(onSubmit)();
  };



  if (loading) {
    return (
      <MaxWidthPage>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="bg-white border rounded-lg p-6">
            <div className="h-4 bg-gray-200 rounded w-40 mb-2"></div>
            <div className="h-10 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </MaxWidthPage>
    );
  }

  return (
    <RecentlyViewedProvider>
        <CrawlerPageLayout
          crawlerName={crawlerName}
          callback={handleCrawlerNameChange}
          actionButtons={
            <div className="flex justify-end gap-3 pr-6">
              <Button
                text="CANCEL"
                className="button button-secondary rounded"
                onClick={() => navigate('/crawlers')}
                disabled={isSubmitting}
              />
              <Button
                text="REVIEW CHANGES"
                className="button button-primary rounded"
                onClick={reviewChanges}
                disabled={isSubmitting}
              />
            </div>
          }
        >
          <FormProvider {...methods}>
            <CrawlerCostProvider formValues={watchedValues}>
              <CrawlerUrlInput
                register={register}
                setValue={setValue}
                errors={errors}
                watchedValues={watchedValues}
                isLoading={loading}
              />

              <CrawlerFormSections
                register={register}
                setValue={setValue}
                errors={errors}
                watchedValues={watchedValues}
                testCallback={testCallback}
                isTestingCallback={isTestingCallback}
                callbackTestResult={callbackTestResult}
                setCallbackTestResult={setCallbackTestResult}
              />
            </CrawlerCostProvider>
          </FormProvider>

          {/* Summary Modal */}
          <CrawlerSummaryModal
            isOpen={showSummaryModal}
            onClose={() => setShowSummaryModal(false)}
            onConfirm={confirmSaveChanges}
            formValues={watchedValues}
            crawlerName={crawlerName}
            confirmButtonText="SAVE CHANGES"
          />
        </CrawlerPageLayout>
      </RecentlyViewedProvider>
  );
};

export default EditCrawlerPage;
